<script setup lang="ts">
import { defineModel, onUnmounted, ref, watch } from 'vue';
import Core from '@/core/core/core';
import { delHash } from '@/core/utils/route';
import type { Props } from './types';
import { defineTabsStore } from './store';
import TabsViewMenu from './menu.vue';
import TopLoadbar from '@/components/forms/loadbar/loadbar.vue';
import { checkComponentIsLoading } from './utils';

const props = withDefaults(defineProps<Props>(), {
	showMenuInPopup: undefined,
	isShortable: false,
});

const model = defineModel<string>();

const store = defineTabsStore(props, model);

const uid = Math.random();

if (props.pageMod) Core.state.documentClassModificators.set(uid, 'top-hasTabsViewPageMod');

onUnmounted(() => {
	if (props.pageMod) Core.state.documentClassModificators.delete(uid);

	if (store.$id) delHash(store.$id, store.activeItemName, true);
});

const componentRef = ref();
const isLoading = ref(false);

watch([componentRef, () => store.component], () => {
	isLoading.value = checkComponentIsLoading(store.component);
}, { immediate: true });
</script>

<template>
	<div
		:class="{
			'top-tabsView': true,
			'top-tabsView-pageMod': props.pageMod,
			'top-tabsView-inPopup': store.showMenuInPopup,
		}"
	>
		<TabsViewMenu
			:isShortable="isShortable"
			v-model:isLoading="isLoading"
		>
			<!-- @slot Меню, ожидает передачу компонентов TabsViewMenuItem и TabsViewMenuTitle -->
			<slot name="menu"></slot>
		</TabsViewMenu>

		<div
			:class="{
				'top-tabsView_contents': true,
				'top-tabsView_contents-isLoading': isLoading,
				'top-tabsView_contents-noScrollable': !store.scrollable,
			}"
		>
			<TopLoadbar v-if="isLoading"/>

			<keep-alive>
				<component
					ref="componentRef"
					:is="store.component"
				/>
			</keep-alive>
		</div>
	</div>
</template>

<style>
:root {
	--top-tabsView-top: 0px;
	--top-tabsView-menu-width: 220px;
	--top-tabsView-contents-offset-top: 0px;
}

.top-tabsView {
	background: var(--color-layout-middle);
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: row;
}

.top-tabsView_contents {
	border-radius: var(--top-radius-4);
	background: var(--color-layout-front-1);
	padding: var(--top-padding-4);
	flex-grow: 1;
	overflow: auto;
	position: relative;
}

.top-tabsView_contents-isLoading {
	pointer-events: none;
}

.top-tabsView_contents-noScrollable {
	width: calc(100% - var(--top-tabsView-menu-width));
	padding: 0;
	overflow: visible;
}

/* top-tabsView-inPopup */
.top-tabsView-inPopup {
	background: var(--color-layout-front-1);
	flex-direction: column;
}

.top-tabsView-inPopup > .top-tabsView_contents {
	border-radius: 0;
}

.top-tabsView-inPopup > .top-tabsView_contents-noScrollable {
	width: auto;
}
</style>
